import { Box } from '@/shared/ui';

import { Z_INDEX } from '@/config/z-index';
import { normalizeSx } from '@/shared/lib/normalizeSx';

import type { Sx } from '@/shared/types/uiTypes';

import PremiumFeatureMessage from './PremiumFeatureMessage';
import UpgradeToPremiumButton from './UpgradeToPremiumButton';
import type { ButtonType } from './UpgradeToPremiumButton';

type PremiumGateContentProps = {
  buttonSx?: Sx;
  buttonType?: ButtonType;
  messageSx?: Sx;
  showButton?: boolean;
  sx?: Sx;
};

type InlinePremiumAccessGateProps = PremiumGateContentProps & {
  variant?: 'inline';
};

type PreviewPremiumAccessGateProps = PremiumGateContentProps & {
  backgroundImage?: string;
  backgroundRepeat?: string;
  backgroundSize?: string;
  blur?: string;
  contentSx?: Sx;
  height: string | number;
  imageTransform?: string;
  overlayColor?: string;
  variant: 'preview';
};

type AbsolutePremiumAccessGateProps = {
  backdropSx?: Sx;
  contentSx?: Sx;
  messageSx?: Sx;
  top?: string | number;
  variant: 'absolute';
};

type PremiumAccessGateProps =
  | InlinePremiumAccessGateProps
  | PreviewPremiumAccessGateProps
  | AbsolutePremiumAccessGateProps;

const previewImageUrl = "url('/img/preview/history-chart.png')";

const inlineGateSx: Sx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  alignItems: 'center',
  gap: '16px'
};

const contentStackSx: Sx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px'
};

const messageStackSx: Sx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const previewGateSx: Sx = {
  position: 'relative',
  overflow: 'hidden'
};

const previewContentSx: Sx = {
  position: 'relative',
  zIndex: Z_INDEX.raised,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  alignItems: 'center',
  gap: '8px'
};

const absoluteBackdropSx: Sx = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backdropFilter: 'blur(4px)',
  zIndex: Z_INDEX.blurBackdrop
};

const absoluteContentSx: Sx = {
  position: 'absolute',
  left: '50%',
  textAlign: 'center',
  transform: 'translateX(-50%)',
  zIndex: Z_INDEX.blurBackdropContent,
  width: '100%'
};

const PremiumGateContent = ({
  buttonSx,
  buttonType = 'upgrade',
  messageSx,
  showButton = true,
  sx
}: PremiumGateContentProps) => (
  <Box sx={[contentStackSx, ...normalizeSx(sx)]}>
    <Box sx={messageStackSx}>
      <PremiumFeatureMessage sx={messageSx} />
    </Box>
    {showButton && (
      <UpgradeToPremiumButton
        sx={buttonSx}
        type={buttonType}
      />
    )}
  </Box>
);

const PremiumAccessGate = (props: PremiumAccessGateProps) => {
  if (props.variant === 'absolute') {
    const { backdropSx, contentSx, messageSx, top = '20%' } = props;

    return (
      <>
        <Box sx={[absoluteBackdropSx, ...normalizeSx(backdropSx)]} />
        <Box sx={[absoluteContentSx, { top }, ...normalizeSx(contentSx)]}>
          <PremiumGateContent
            messageSx={messageSx}
            showButton={false}
          />
        </Box>
      </>
    );
  }

  if (props.variant === 'preview') {
    const {
      backgroundImage = previewImageUrl,
      backgroundRepeat = 'no-repeat',
      backgroundSize = 'cover',
      blur = '4px',
      buttonSx,
      buttonType,
      contentSx,
      height,
      imageTransform = 'scale(1)',
      messageSx,
      overlayColor,
      showButton,
      sx
    } = props;

    return (
      <Box
        sx={[
          previewGateSx,
          {
            '&:before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage,
              backgroundPosition: 'center',
              backgroundRepeat,
              backgroundSize,
              filter: `blur(${blur})`,
              transform: imageTransform,
              zIndex: Z_INDEX.base
            }
          },
          ...normalizeSx(sx)
        ]}
      >
        <PremiumGateContent
          buttonSx={buttonSx}
          buttonType={buttonType}
          messageSx={messageSx}
          showButton={showButton}
          sx={[
            previewContentSx,
            {
              backgroundColor: overlayColor,
              height
            },
            ...normalizeSx(contentSx)
          ]}
        />
      </Box>
    );
  }

  const { buttonSx, buttonType, messageSx, showButton, sx } = props;

  return (
    <PremiumGateContent
      buttonSx={buttonSx}
      buttonType={buttonType}
      messageSx={messageSx}
      showButton={showButton}
      sx={[inlineGateSx, ...normalizeSx(sx)]}
    />
  );
};

export default PremiumAccessGate;
